import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { tokenStore } from "@/lib/token";
import { X, Image as ImageIcon, Type, CreditCard, Trash2, Save, GripHorizontal, Pencil, Monitor, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";

const DESKTOP_CANVAS_WIDTH = 1200;
const MOBILE_CANVAS_WIDTH = 390;
const DEFAULT_CANVAS_HEIGHT = 1400;
const DEFAULT_MOBILE_CANVAS_HEIGHT = 1800;

const uid = () => Math.random().toString(36).slice(2, 10);

const uploadImage = async (file, hdr) => {
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const r = await axios.post(`${API}/upload/image`, { image: dataUrl }, { headers: hdr });
  return r.data.url;
};

const pickImage = (onPicked) => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";
  input.onchange = (e) => { const file = e.target.files[0]; if (file) onPicked(file); };
  input.click();
};

// Empile les blocs du bureau en une colonne pleine largeur, pour donner un
// point de départ raisonnable en mode téléphone plutôt qu'un canevas vide.
// La hauteur est mise à l'échelle avec la largeur pour garder les proportions
// des images ; le bloc abonnement garde une hauteur fixe lisible.
const deriveMobileBlocks = (desktopBlocks) => {
  const innerWidth = MOBILE_CANVAS_WIDTH - 32;
  const scale = innerWidth / DESKTOP_CANVAS_WIDTH;
  let y = 24;
  return desktopBlocks
    .slice()
    .sort((a, b) => a.y - b.y)
    .map((block) => {
      const height = block.type === "pricing" ? 560 : Math.max(60, Math.round(block.height * Math.max(scale, 0.5)));
      const placed = { ...block, id: uid(), x: 16, y, width: innerWidth, height };
      y += height + 20;
      return placed;
    });
};

/**
 * Editeur de page produit en glisser-déposer libre (comme Canva) : les blocs
 * (image, texte, abonnement) sont librement déplaçables et redimensionnables,
 * avec une mise en page indépendante pour Ordinateur et Téléphone (bascule en
 * haut à droite). Le fond, le logo, le titre et la description du bandeau du
 * haut restent, eux, à une position fixe (non déplaçable) mais leur contenu
 * est modifiable en direct juste au-dessus du canevas, avec un aperçu qui
 * reprend exactement les mêmes points de rupture que la page publique
 * (BotDetailPage.js : hauteur de bannière, disposition, taille du titre).
 * Le bouton retour et le sous-titre ne sont pas modifiables ici.
 */
export default function PageBuilder({ bot, onClose, onSaved }) {
  const [device, setDeviceState] = useState("desktop");
  const [desktopBlocks, setDesktopBlocks] = useState(() => bot.page_layout?.blocks || []);
  const [desktopCanvasHeight, setDesktopCanvasHeight] = useState(bot.page_layout?.canvas_height || DEFAULT_CANVAS_HEIGHT);
  const [mobileBlocks, setMobileBlocks] = useState(() => bot.page_layout?.mobile_blocks || []);
  const [mobileCanvasHeight, setMobileCanvasHeight] = useState(bot.page_layout?.mobile_canvas_height || DEFAULT_MOBILE_CANVAS_HEIGHT);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [heroTitle, setHeroTitle] = useState(bot.name || "");
  const [heroDescription, setHeroDescription] = useState(bot.description || "");
  const [heroLogo, setHeroLogo] = useState(bot.image_url || "");
  const [heroBanner, setHeroBanner] = useState(bot.banner_url || "");
  const dragRef = useRef(null);

  const isMobile = device === "mobile";
  const canvasWidth = isMobile ? MOBILE_CANVAS_WIDTH : DESKTOP_CANVAS_WIDTH;
  const blocks = isMobile ? mobileBlocks : desktopBlocks;
  const setBlocks = isMobile ? setMobileBlocks : setDesktopBlocks;
  const canvasHeight = isMobile ? mobileCanvasHeight : desktopCanvasHeight;
  const setCanvasHeight = isMobile ? setMobileCanvasHeight : setDesktopCanvasHeight;

  const switchDevice = (next) => {
    if (next === "mobile" && mobileBlocks.length === 0 && desktopBlocks.length > 0) {
      setMobileBlocks(deriveMobileBlocks(desktopBlocks));
    }
    setSelectedId(null);
    setDeviceState(next);
  };

  const hdr = () => ({ Authorization: `Bearer ${tokenStore.get()}` });

  const updateBlock = (id, patch) => setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const removeBlock = (id) => {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
    setSelectedId((current) => (current === id ? null : current));
  };
  const addBlock = (block) => {
    setBlocks((prev) => [...prev, block]);
    setSelectedId(block.id);
  };

  const startDrag = (e, block, mode) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedId(block.id);
    const start = { x: e.clientX, y: e.clientY, left: block.x, top: block.y, width: block.width, height: block.height };
    dragRef.current = { id: block.id, mode };

    const onMove = (ev) => {
      if (!dragRef.current) return;
      const dx = ev.clientX - start.x;
      const dy = ev.clientY - start.y;
      if (dragRef.current.mode === "move") {
        updateBlock(block.id, { x: Math.max(0, Math.round(start.left + dx)), y: Math.max(0, Math.round(start.top + dy)) });
      } else {
        updateBlock(block.id, { width: Math.max(40, Math.round(start.width + dx)), height: Math.max(30, Math.round(start.height + dy)) });
      }
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const offset = () => 40 + (blocks.length % 6) * 24;

  const addImageBlock = () => pickImage(async (file) => {
    try {
      const url = await uploadImage(file, hdr());
      addBlock({ id: uid(), type: "image", x: offset(), y: offset(), width: isMobile ? canvasWidth - 32 : 320, height: 220, url, objectFit: "cover" });
    } catch { toast.error("Erreur upload"); }
  });

  const addTextBlock = () => {
    addBlock({ id: uid(), type: "text", x: offset(), y: offset(), width: isMobile ? canvasWidth - 32 : 360, height: 120, content: "Nouveau texte", fontSize: 16, color: "#ffffff", align: "left", weight: "normal" });
  };

  const hasPricingBlock = blocks.some((b) => b.type === "pricing");
  const addPricingBlock = () => {
    if (hasPricingBlock) { toast.error("Un seul bloc Abonnement est possible"); return; }
    addBlock({ id: uid(), type: "pricing", x: offset(), y: offset(), width: isMobile ? canvasWidth - 32 : 420, height: 560 });
  };

  const replaceImage = (block) => pickImage(async (file) => {
    try {
      const url = await uploadImage(file, hdr());
      updateBlock(block.id, { url });
    } catch { toast.error("Erreur upload"); }
  });

  const replaceHeroBanner = () => pickImage(async (file) => {
    try { setHeroBanner(await uploadImage(file, hdr())); } catch { toast.error("Erreur upload"); }
  });
  const replaceHeroLogo = () => pickImage(async (file) => {
    try { setHeroLogo(await uploadImage(file, hdr())); } catch { toast.error("Erreur upload"); }
  });

  const save = async () => {
    if (!heroTitle.trim()) { toast.error("Le titre du produit ne peut pas être vide"); return; }
    setSaving(true);
    try {
      const page_layout = {
        canvas_height: desktopCanvasHeight,
        blocks: desktopBlocks,
        mobile_canvas_height: mobileCanvasHeight,
        mobile_blocks: mobileBlocks,
      };
      const patch = { page_layout, name: heroTitle.trim(), description: heroDescription, image_url: heroLogo, banner_url: heroBanner };
      await axios.put(`${API}/admin/shop/bots/${bot.id}`, patch, { headers: hdr() });
      toast.success("Mise en page enregistrée");
      onSaved?.(patch);
    } catch { toast.error("Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  };

  const selected = blocks.find((b) => b.id === selectedId);

  return (
    <div className="fixed inset-0 z-[200] bg-[#0a0a0f] flex flex-col">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 bg-[#12121a] px-4 py-3 shrink-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-white/70 mr-2">Personnaliser — {bot.name}</span>
          <Button data-testid="builder-add-image" size="sm" onClick={addImageBlock} variant="outline" className="border-white/10 text-xs h-8"><ImageIcon className="w-3.5 h-3.5 mr-1.5" />Image</Button>
          <Button data-testid="builder-add-text" size="sm" onClick={addTextBlock} variant="outline" className="border-white/10 text-xs h-8"><Type className="w-3.5 h-3.5 mr-1.5" />Texte</Button>
          <Button data-testid="builder-add-pricing" size="sm" onClick={addPricingBlock} disabled={hasPricingBlock} variant="outline" className="border-white/10 text-xs h-8 disabled:opacity-30"><CreditCard className="w-3.5 h-3.5 mr-1.5" />Abonnement</Button>
          <div className="flex items-center gap-1.5 ml-2 text-xs text-white/40">
            Hauteur
            <input
              type="number"
              value={canvasHeight}
              onChange={(e) => setCanvasHeight(Math.max(600, parseInt(e.target.value, 10) || 600))}
              className="w-20 bg-black/30 border border-white/10 rounded px-2 py-1 text-white"
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-black/30 border border-white/10 rounded-md p-0.5">
            <button
              type="button"
              data-testid="builder-device-desktop"
              onClick={() => switchDevice("desktop")}
              title="Aperçu ordinateur"
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs ${!isMobile ? "bg-blue-600 text-white" : "text-white/40 hover:text-white"}`}
            >
              <Monitor className="w-3.5 h-3.5" />Ordinateur
            </button>
            <button
              type="button"
              data-testid="builder-device-mobile"
              onClick={() => switchDevice("mobile")}
              title="Aperçu téléphone"
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs ${isMobile ? "bg-blue-600 text-white" : "text-white/40 hover:text-white"}`}
            >
              <Smartphone className="w-3.5 h-3.5" />Téléphone
            </button>
          </div>
          <Button data-testid="builder-save" size="sm" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-500 text-xs h-8">
            <Save className="w-3.5 h-3.5 mr-1.5" />{saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
          <button data-testid="builder-close" onClick={onClose} className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
      </div>

      <p className="text-center text-[11px] text-white/30 py-2 shrink-0 px-4">
        Cliquez sur un bloc pour le sélectionner, tirez sa poignée bleue pour le déplacer et son coin pour le redimensionner.
        Le fond, le logo, le titre et la description du bandeau ci-dessous sont modifiables mais restent à une position fixe.
        {isMobile && " Mode téléphone : mise en page indépendante de la version ordinateur — générée à partir d'elle la première fois, puis modifiable librement."}
      </p>

      <div className="flex-1 overflow-auto flex flex-col items-center py-6" onPointerDown={() => setSelectedId(null)}>
        {/* Bandeau du haut : position fixe (non déplaçable), contenu modifiable avec aperçu en direct.
            Les seules différences réelles entre ordinateur et téléphone sur la page publique
            (BotDetailPage.js) sont la hauteur de la bannière, la disposition (colonne/ligne) et
            la taille du titre — reproduites ici à l'identique pour un aperçu fidèle. */}
        <div style={{ width: canvasWidth, maxWidth: "100%" }} className="shrink-0 mb-3" onPointerDown={(e) => e.stopPropagation()}>
          <div className={`relative overflow-hidden group border border-white/10 ${isMobile ? "h-40" : "h-56"}`}>
            {heroBanner ? (
              <img src={heroBanner} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full" style={{ backgroundColor: bot.color || "#5865F2" }} />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d14] via-[#0d0d14]/50 to-transparent pointer-events-none" />
            <button
              type="button"
              data-testid="hero-edit-banner"
              onClick={replaceHeroBanner}
              className="absolute inset-0 flex items-center justify-center gap-2 bg-black/0 group-hover:bg-black/50 text-transparent group-hover:text-white text-sm font-medium transition-all"
            >
              <ImageIcon className="w-4 h-4" />Changer le fond
            </button>
          </div>

          <div className={`px-2 relative z-10 flex gap-4 ${isMobile ? "flex-col items-start -mt-10" : "items-end -mt-14"}`}>
            <button
              type="button"
              data-testid="hero-edit-logo"
              onClick={replaceHeroLogo}
              className="w-20 h-20 shrink-0 rounded-full border-4 border-[#0d0d14] overflow-hidden relative group/logo bg-white/10 flex items-center justify-center"
              title="Changer le logo"
            >
              {heroLogo ? (
                <img src={heroLogo} alt="" className="w-full h-full object-cover" />
              ) : (
                <ImageIcon className="w-7 h-7 text-white/30" />
              )}
              <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover/logo:bg-black/50 transition-colors">
                <Pencil className="w-5 h-5 text-transparent group-hover/logo:text-white transition-colors" />
              </div>
            </button>

            <div className="flex-1 pb-3 w-full">
              <input
                data-testid="hero-edit-title"
                value={heroTitle}
                onChange={(e) => setHeroTitle(e.target.value)}
                placeholder="Titre du produit"
                className={`w-full bg-transparent border-b border-dashed border-white/15 hover:border-white/40 focus:border-blue-500 outline-none font-heading font-bold text-white pb-1 placeholder:text-white/20 ${isMobile ? "text-xl" : "text-3xl"}`}
              />
              <textarea
                data-testid="hero-edit-description"
                value={heroDescription}
                onChange={(e) => setHeroDescription(e.target.value)}
                placeholder="Description du produit"
                rows={2}
                className="w-full bg-transparent border-b border-dashed border-white/15 hover:border-white/40 focus:border-blue-500 outline-none text-sm text-white/70 resize-none mt-2 pb-1 placeholder:text-white/20"
              />
            </div>
          </div>
        </div>

        <div
          style={{
            width: canvasWidth,
            height: canvasHeight,
            position: "relative",
            flexShrink: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
            backgroundSize: "20px 20px",
            backgroundColor: "#0d0d14",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          {blocks.map((block) => (
            <div
              key={block.id}
              data-testid={`builder-block-${block.type}`}
              style={{ position: "absolute", left: block.x, top: block.y, width: block.width, height: block.height }}
              className={selectedId === block.id ? "ring-2 ring-blue-500" : "ring-1 ring-white/10 hover:ring-white/30"}
            >
              {/* Poignée de déplacement : toujours au-dessus du bloc, pour ne jamais
                  gêner la saisie de texte ou le clic sur le contenu du bloc. */}
              <div
                data-testid="builder-block-handle"
                onPointerDown={(e) => startDrag(e, block, "move")}
                title="Déplacer"
                className="absolute left-0 right-0 h-5 -top-5 rounded-t bg-blue-600/90 hover:bg-blue-500 cursor-move flex items-center justify-center"
              >
                <GripHorizontal className="w-3.5 h-3.5 text-white/80" />
              </div>

              <div className="w-full h-full" onPointerDown={(e) => e.stopPropagation()} onClick={() => setSelectedId(block.id)}>
                {block.type === "image" && (
                  <img src={block.url} alt="" className="w-full h-full pointer-events-none select-none" style={{ objectFit: block.objectFit || "cover" }} />
                )}
                {block.type === "text" && (
                  <textarea
                    value={block.content}
                    onChange={(e) => updateBlock(block.id, { content: e.target.value })}
                    style={{ fontSize: block.fontSize, color: block.color, textAlign: block.align, fontWeight: block.weight, backgroundColor: "transparent" }}
                    className="w-full h-full resize-none outline-none p-2"
                  />
                )}
                {block.type === "pricing" && (
                  <div className="w-full h-full bg-white/5 border border-dashed border-white/20 flex items-center justify-center pointer-events-none">
                    <div className="text-center text-white/40 text-xs px-4">
                      <CreditCard className="w-6 h-6 mx-auto mb-2" />
                      Bloc Abonnement (fonctionnel)<br />le vrai sélecteur de plans s'affiche ici sur la page publique
                    </div>
                  </div>
                )}
              </div>

              {selectedId === block.id && (
                <>
                  <div
                    onPointerDown={(e) => { e.stopPropagation(); removeBlock(block.id); }}
                    style={{ position: "absolute", top: -12, right: -12, width: 24, height: 24, borderRadius: 9999 }}
                    className="bg-red-500 hover:bg-red-400 flex items-center justify-center cursor-pointer"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-white" />
                  </div>
                  <div
                    data-testid="builder-block-resize"
                    onPointerDown={(e) => startDrag(e, block, "resize")}
                    style={{ position: "absolute", bottom: -6, right: -6, width: 16, height: 16, borderRadius: 2 }}
                    className="bg-blue-500 cursor-nwse-resize"
                  />
                </>
              )}
            </div>
          ))}
          {blocks.length === 0 && (
            <p className="absolute inset-0 flex items-center justify-center text-white/20 text-sm pointer-events-none px-6 text-center">
              {isMobile
                ? "Ajoutez un bloc, ou repassez en Ordinateur puis revenez ici pour générer une base à partir de cette mise en page"
                : "Ajoutez une image, un texte ou le bloc abonnement pour commencer"}
            </p>
          )}
        </div>
      </div>

      {selected?.type === "text" && (
        <div className="absolute right-6 top-24 w-56 bg-[#0f1019] border border-white/[0.09] rounded-2xl p-4 space-y-3 shadow-xl">
          <div>
            <label className="text-[11px] text-white/40">Taille du texte</label>
            <input type="number" value={selected.fontSize} onChange={(e) => updateBlock(selected.id, { fontSize: parseInt(e.target.value, 10) || 16 })} className="w-full bg-black/30 border border-white/10 rounded px-2 py-1 text-sm text-white mt-1" />
          </div>
          <div>
            <label className="text-[11px] text-white/40">Couleur</label>
            <input type="color" value={selected.color} onChange={(e) => updateBlock(selected.id, { color: e.target.value })} className="w-full h-8 bg-black/30 border border-white/10 rounded mt-1" />
          </div>
          <div>
            <label className="text-[11px] text-white/40 block mb-1">Alignement</label>
            <div className="flex gap-1">
              {[["left", "G"], ["center", "C"], ["right", "D"]].map(([value, label]) => (
                <button key={value} onClick={() => updateBlock(selected.id, { align: value })} className={`flex-1 text-xs py-1 rounded ${selected.align === value ? "bg-blue-600" : "bg-black/30"}`}>{label}</button>
              ))}
            </div>
          </div>
          <button onClick={() => updateBlock(selected.id, { weight: selected.weight === "bold" ? "normal" : "bold" })} className={`w-full text-xs py-1.5 rounded font-bold ${selected.weight === "bold" ? "bg-blue-600" : "bg-black/30"}`}>Gras</button>
        </div>
      )}

      {selected?.type === "image" && (
        <div className="absolute right-6 top-24 w-56 bg-[#0f1019] border border-white/[0.09] rounded-2xl p-4 shadow-xl">
          <Button size="sm" onClick={() => replaceImage(selected)} variant="outline" className="w-full border-white/10 text-xs">Remplacer l'image</Button>
        </div>
      )}
    </div>
  );
}
